import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Folder as FolderIcon, Home, Loader2, Lock, Search } from 'lucide-react';
import { documentService } from '../../services/documentService';
import { logger } from '../../utils/logger';
import { useLanguage } from '../../i18n';

/**
 * The "Select & Activate" folder destination list, shared by every surface that
 * asks "where should this file live?".
 *
 * Extracted from MoveToFolderModal (Move folders #255) when the chat's Manage
 * Document dialog needed the same picker (Name saving failure front end #330) —
 * one tree fetch, one row vocabulary, one set of testids, so the two surfaces
 * can never disagree about which destinations are offered.
 *
 * Destinations come from `GET /api/folders/tree` (ONE request for the whole
 * tree; `/contents` would need one per node). Root is a real destination
 * (folder_id = null) and is part of the list. Two rows are deliberately shown
 * but NOT selectable:
 *   - the document's CURRENT folder — moving there is a no-op, and
 *   - PROTECTED /Financials system folders (#145), which are system-managed and
 *     would silently re-route a manually placed file.
 * Showing them with a marker (rather than hiding them) matches how the File
 * Manager already renders them.
 *
 * Purely presentational + self-fetching: the parent owns the selection and what
 * to do with it.
 */
export const ROOT_KEY = '__root__';

const FolderDestinationPicker = ({
    currentFolderId = null,
    selectedKey,
    onSelect,
    listClassName = 'max-h-56',
    // Root is a legitimate destination everywhere, but when the caller has no
    // idea where the document currently lives we must not mark ANY row current.
    knowsCurrentFolder = true,
    // Bump to re-fetch the tree (e.g. after the caller creates a new folder, so
    // it becomes selectable without closing the dialog).
    refreshToken = 0,
    // Bulk Move (#393): the caller may know more than "the current folder" —
    // for a SET of items a destination can be invalid because it is one of the
    // selected folders, inside one, or already holds every item. `blockedReason`
    // maps a row's folder id (null = root) to 'current' | 'self' | 'inside' |
    // null; a blocked row is shown but not selectable, like the current folder.
    blockedReason = null,
    // Hands the loaded tree rows to the caller (the Move dialog needs them to
    // work out descendants without a second request).
    onTreeLoaded = null,
}) => {
    const { t } = useLanguage();
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await documentService.getFolderTree();
                if (!cancelled) {
                    setFolders(res?.folders || []);
                    onTreeLoaded?.(res?.folders || []);
                }
            } catch (e) {
                logger.error('MOVE', 'Failed to load folder tree', e);
                if (!cancelled) setError(t('fmCouldNotLoadFolders', 'Could not load your folders.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` and the onTreeLoaded callback are intentionally omitted: a language switch / parent re-render must not refetch the tree
    }, [refreshToken]);

    const rows = useMemo(() => {
        const rootName = t('fmMyDocuments', 'My Documents');
        const all = [
            { key: ROOT_KEY, id: null, name: rootName, path: rootName, depth: 0, isProtected: false, isRoot: true },
            ...folders.map((f) => ({
                key: f.id,
                id: f.id,
                name: f.name,
                path: f.path || f.name,
                depth: f.depth || 0,
                isProtected: !!f.is_protected,
                isRoot: false,
            })),
        ];
        const needle = filter.trim().toLowerCase();
        if (!needle) return all;
        return all.filter((r) => r.path.toLowerCase().includes(needle));
    }, [folders, filter, t]);

    const isCurrent = useCallback(
        (row) => (knowsCurrentFolder ? (row.isRoot ? !currentFolderId : row.id === currentFolderId) : false),
        [currentFolderId, knowsCurrentFolder],
    );

    return (
        <div className="flex flex-col min-h-0">
            <div className="relative mb-2">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none z-10" />
                <input
                    data-testid="move-folder-search"
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder={t('fmSearchFoldersPlaceholder', 'Search folders...')}
                    className="pai-input w-full h-[42px] pl-10 pr-3 text-[14px] placeholder:text-pai-placeholder"
                />
            </div>

            {error && (
                <div data-testid="move-folder-error" className="mb-2 text-xs text-pai-red">{error}</div>
            )}

            <div className={`overflow-y-auto min-h-0 ${listClassName}`}>
                {loading ? (
                    <div className="flex items-center justify-center py-8 text-white/40">
                        <Loader2 size={20} className="animate-spin" />
                    </div>
                ) : rows.length === 0 ? (
                    <div data-testid="move-folder-empty" className="py-8 text-center text-[14.5px] text-white/40">
                        {t('fmNoFoldersMatch', 'No folders match that search.')}
                    </div>
                ) : (
                    <ul className="space-y-1">
                        {rows.map((row) => {
                            const reason = blockedReason ? blockedReason(row.id) : null;
                            const current = isCurrent(row) || reason === 'current';
                            const blocked = reason && reason !== 'current' ? reason : null;
                            const disabled = current || !!blocked || row.isProtected;
                            const selected = selectedKey === row.key;
                            const Icon = row.isRoot ? Home : FolderIcon;
                            return (
                                <li key={row.key}>
                                    <button
                                        type="button"
                                        data-testid="move-folder-option"
                                        data-folder-id={row.id || ''}
                                        data-disabled={disabled ? 'true' : 'false'}
                                        data-selected={selected ? 'true' : 'false'}
                                        disabled={disabled}
                                        onClick={() => onSelect(row.key)}
                                        style={{ paddingLeft: `${12 + row.depth * 14}px` }}
                                        className={`
                                            w-full flex items-center gap-2.5 pr-3 py-2.5 rounded-row text-left text-[14px] transition-colors border
                                            ${disabled
                                                ? 'border-transparent text-white/30 cursor-not-allowed'
                                                : selected
                                                    ? 'border-white/80 bg-white/[0.055] text-white font-semibold shadow-glow-sm'
                                                    : 'border-transparent text-pai-body hover:bg-white/[0.07] hover:text-white'
                                            }
                                        `}
                                    >
                                        <Icon size={16} className="flex-shrink-0" />
                                        <span className="truncate flex-1">{row.name}</span>
                                        {row.isProtected && (
                                            <Lock data-testid="move-folder-locked" size={13} className="flex-shrink-0 text-white/40" />
                                        )}
                                        {blocked && (
                                            <span
                                                data-testid="move-folder-blocked"
                                                data-reason={blocked}
                                                className="text-[11px] font-semibold uppercase tracking-[0.06em] text-pai-meta flex-shrink-0"
                                            >
                                                {blocked === 'self'
                                                    ? t('fmMoveSelectedFolder', 'Selected')
                                                    : t('fmMoveInsideSelection', 'Inside selection')}
                                            </span>
                                        )}
                                        {current && (
                                            <span
                                                data-testid="move-folder-current"
                                                className="text-[11px] font-semibold uppercase tracking-[0.06em] text-pai-meta flex-shrink-0"
                                            >
                                                {t('fmCurrent', 'Current')}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default FolderDestinationPicker;
